import {CalendarIcon} from '@heroicons/react/outline';
import moment from 'moment';
import React from 'react';

import ActionIcons from '@/components/AppReleases/ActionIcons';
import VersionBadge from '@/components/AppReleases/VersionBadge';
import Modal from '@/components/Modals';
import type {AppDistributionRelease} from '@/types/FirebaseAppDistribution';

interface ReleaseDetailsModalProps {
  release: AppDistributionRelease | null;
  isOpen: boolean;
  onClose: () => void;
}

const ReleaseDetailsModal: React.FC<ReleaseDetailsModalProps> = ({
  release,
  isOpen,
  onClose,
}) => {
  if (!release) {
    return null;
  }

  const createdDate = new Date(release.createTime);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Release Details"
      width="w-11/12 md:w-5/12 lg:w-4/12 xl:w-3/12 px-4 sm:px-0"
      closeOnOutsideClick
    >
      <div className="space-y-6">
        {/* Version Badge Header */}
        <div className="flex items-center justify-center">
          <VersionBadge
            version={release.displayVersion}
            buildVersion={release.buildVersion}
            showBuildVersion={true}
          />
        </div>

        {/* Release Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Display Version
            </label>
            <p className="text-sm text-gray-900 dark:text-white">
              {release.displayVersion}
            </p>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Build Version
            </label>
            <p className="text-sm text-gray-900 dark:text-white">
              {release.buildVersion}
            </p>
          </div>
        </div>

        {/* Timestamps */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1">
              <CalendarIcon className="w-4 h-4" />
              Created
            </label>
            <p className="text-sm text-gray-900 dark:text-white">
              {moment(createdDate).format('MMMM D, YYYY [at] h:mm A')}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {moment(createdDate).fromNow()}
            </p>
          </div>
        </div>

        {/* Release Notes */}
        {release.releaseNotes?.text && (
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Release Notes
            </label>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-md p-4 max-h-40 overflow-y-auto">
              <pre className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap font-sans">
                {release.releaseNotes.text}
              </pre>
            </div>
          </div>
        )}

        {/* Download Actions */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-600">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Use the download options to get the release files or access the
            Firebase console.
          </div>
          <div className="flex items-center gap-2">
            <ActionIcons
              binaryDownloadUri={release.binaryDownloadUri}
              firebaseConsoleUri={release.firebaseConsoleUri}
              testingUri={release.testingUri}
              fileName={`${release.displayVersion}-${release.buildVersion}`}
              size="lg"
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ReleaseDetailsModal;
